import { useCallback, useRef, useState } from 'react';
import {
  Annotation,
  AnnotationPoint,
  AnnotationType,
  ArrowAnnotation,
  CircleAnnotation,
  DragContext,
  DragMode,
  HandleInfo,
  HitTestParams,
  MoveAnnotationParams,
  PenAnnotation,
  RectangleAnnotation,
  ResizeAnnotationParams,
  TextAnnotation,
} from '../types';
import {
  ERASER_TOOL,
  HANDLE_HIT_RADIUS,
  HIGHLIGHTER_OPACITY,
  POINTER_TOOL,
  SHAPE_MIN_SIZE_FRACTION,
  TEXT_DEFAULT_FONT_SIZE,
} from '../constants';
import { getBBox, getHandles, hitTest, moveAnnotation, newAnnotationId, resizeAnnotation } from '@v2/components/imageHandling/components/DrawingCanvas/helpers';
import type { ManageDrawingCanvasApi } from './useManageDrawingCanvas';

export interface UsePointerInteractionsParams {
  api: ManageDrawingCanvasApi;
  /** Minimum dimension used as the hit threshold base (width of the image in working space). */
  hitThresholdBase: number;
  onRequestTextEditing: (params: { annotationId: string; isNew: boolean; clientX: number; clientY: number; selectAll?: boolean }) => void;
  /** Optional pan handlers — called when the Pointer tool clicks empty space so the underlying image pans instead. */
  onBackgroundPanStart?: (e: { clientX: number; clientY: number }) => void;
  onBackgroundPanMove?: (e: { clientX: number; clientY: number }) => void;
  onBackgroundPanEnd?: () => void;
  /** Called when a double-click / double-tap lands on empty space so the underlying image can zoom. */
  onBackgroundDoubleClick?: (e: { clientX: number; clientY: number }) => void;
}

const nowIso = (): string => new Date().toISOString();

const findTopmostHit = ({ annotations, hitParams }: { annotations: Annotation[]; hitParams: Omit<HitTestParams, 'annotation'> }): Annotation | null => {
  for (let i = annotations.length - 1; i >= 0; i--) {
    if (hitTest({ annotation: annotations[i], ...hitParams })) return annotations[i];
  }
  return null;
};

/**
 * Pointer/gesture pipeline for the drawing overlay. Web uses pointer events;
 * native uses the same callbacks wired through react-native-gesture-handler
 * by the caller (AnnotationLayer). This hook is the single source of truth
 * for "what happens when the pointer moves / down / up in an annotated image".
 */
export const usePointerInteractions = ({
  api,
  hitThresholdBase,
  onRequestTextEditing,
  onBackgroundPanStart,
  onBackgroundPanMove,
  onBackgroundPanEnd,
  onBackgroundDoubleClick,
}: UsePointerInteractionsParams) => {
  const drag = useRef<DragContext>({
    dragStartSnapshot: null,
    dragStartPoint: null,
    activeHandle: null,
    drawStartPoint: null,
    currentDraft: null,
  });
  const pointerIdsRef = useRef<Set<number>>(new Set());
  const isBackgroundPanningRef = useRef<boolean>(false);
  const [hoveredAnnotationId, setHoveredAnnotationId] = useState<string | null>(null);
  const [hoveredHandle, setHoveredHandle] = useState<HandleInfo | null>(null);
  const [draftAnnotation, setDraftAnnotation] = useState<Annotation | null>(null);

  const threshold = hitThresholdBase * 0.022;

  const updateDraft = useCallback((next: Annotation | null) => {
    drag.current.currentDraft = next;
    setDraftAnnotation(next);
  }, []);

  const findHandleNear = useCallback(
    (point: AnnotationPoint, annotation: Annotation): { handle: HandleInfo | null; distance: number } => {
      const handles = getHandles(annotation);
      let closest: HandleInfo | null = null;
      let closestDist = Infinity;
      for (const h of handles) {
        const d = Math.hypot(h.x - point.x, h.y - point.y);
        if (d < closestDist) {
          closestDist = d;
          closest = h;
        }
      }
      return { handle: closest, distance: closestDist };
    },
    []
  );

  const handlePointerDown = useCallback(
    (point: AnnotationPoint, e: { shiftKey?: boolean; clientX: number; clientY: number; pointerId?: number; pointerCount?: number }) => {
      if (e.pointerId != null) pointerIdsRef.current.add(e.pointerId);
      if ((e.pointerCount ?? pointerIdsRef.current.size) > 1) return;

      const { annotations, selectedId, activeTool, strokeColor, strokeWidth, fillActive, currentUserId, documentId, pageNumber } = api;

      // === Pointer tool: selection / move / resize only, never draws ===
      if (activeTool === POINTER_TOOL) {
        // 1. Resize handles on the currently-selected annotation take priority.
        if (selectedId) {
          const sel = annotations.find((a) => a.id === selectedId);
          if (sel) {
            const { handle, distance } = findHandleNear(point, sel);
            const handleHitBase = Math.max(threshold, hitThresholdBase * HANDLE_HIT_RADIUS);
            if (handle && distance < handleHitBase) {
              api.pushUndoSnapshot();
              drag.current.dragStartSnapshot = sel;
              drag.current.activeHandle = handle.id;
              drag.current.dragStartPoint = point;
              api.setDragMode(DragMode.Resize);
              return;
            }
          }
        }

        // 2. Topmost annotation at this point determines what the click means.
        const topHit = findTopmostHit({ annotations, hitParams: { pointerPosition: point, threshold } });

        // 2a. Different annotation underneath — select it (allows picking an inner annotation nested inside the current selection).
        if (topHit && topHit.id !== selectedId) {
          api.setSelectedId(topHit.id);
          return;
        }

        // 2b. Topmost is the already-selected one — start a move drag.
        if (topHit && topHit.id === selectedId) {
          api.pushUndoSnapshot();
          drag.current.dragStartSnapshot = topHit;
          drag.current.dragStartPoint = point;
          api.setDragMode(DragMode.Move);
          return;
        }

        // 3. Nothing hit — deselect and start a background pan so the underlying image still pans on empty drag.
        if (selectedId) api.setSelectedId(null);
        if (onBackgroundPanStart) {
          isBackgroundPanningRef.current = true;
          onBackgroundPanStart({ clientX: e.clientX, clientY: e.clientY });
        }
        return;
      }

      // === Eraser: tap to delete ===
      if (activeTool === ERASER_TOOL) {
        const topHit = findTopmostHit({ annotations, hitParams: { pointerPosition: point, threshold } });
        if (topHit) api.deleteAnnotation(topHit.id);
        return;
      }

      // === Drawing tools: always start a draft, ignore existing annotations ===
      if (selectedId) api.setSelectedId(null);

      const style = {
        strokeColor,
        strokeWidth,
        opacity: activeTool === AnnotationType.Highlighter ? HIGHLIGHTER_OPACITY : 1,
      };
      const base = {
        id: newAnnotationId(),
        documentId,
        pageNumber,
        style,
        createdBy: currentUserId,
        createdAt: nowIso(),
        modifiedBy: currentUserId,
        modifiedAt: nowIso(),
      };

      if (activeTool === AnnotationType.Text) {
        const ann: TextAnnotation = {
          ...base,
          type: AnnotationType.Text,
          position: point,
          text: '',
          fontSize: TEXT_DEFAULT_FONT_SIZE,
        };
        api.pushUndoSnapshot();
        api.setAnnotations([...annotations, ann]);
        api.setSelectedId(ann.id);
        api.setActiveTool(POINTER_TOOL);
        onRequestTextEditing({ annotationId: ann.id, isNew: true, clientX: e.clientX, clientY: e.clientY });
        return;
      }

      if (activeTool === AnnotationType.Pen || activeTool === AnnotationType.Highlighter) {
        const ann: PenAnnotation = {
          ...base,
          type: activeTool,
          points: [point],
        } as PenAnnotation;
        updateDraft(ann);
        api.setDragMode(DragMode.Draw);
        return;
      }

      if (activeTool === AnnotationType.Arrow) {
        const ann: ArrowAnnotation = { ...base, type: AnnotationType.Arrow, start: point, end: point };
        drag.current.drawStartPoint = point;
        updateDraft(ann);
        api.setDragMode(DragMode.Draw);
        return;
      }

      if (activeTool === AnnotationType.Rectangle) {
        const ann: RectangleAnnotation = {
          ...base,
          type: AnnotationType.Rectangle,
          origin: point,
          width: 0,
          height: 0,
          filled: fillActive,
        };
        drag.current.drawStartPoint = point;
        updateDraft(ann);
        api.setDragMode(DragMode.Draw);
        return;
      }

      if (activeTool === AnnotationType.Circle) {
        const ann: CircleAnnotation = {
          ...base,
          type: AnnotationType.Circle,
          center: point,
          radiusX: 0,
          radiusY: 0,
          filled: fillActive,
        };
        drag.current.drawStartPoint = point;
        updateDraft(ann);
        api.setDragMode(DragMode.Draw);
        return;
      }
    },
    [api, findHandleNear, hitThresholdBase, onBackgroundPanStart, onRequestTextEditing, threshold, updateDraft]
  );

  const handlePointerMove = useCallback(
    (point: AnnotationPoint, e: { shiftKey?: boolean; ctrlKey?: boolean; metaKey?: boolean; pointerCount?: number; clientX?: number; clientY?: number }) => {
      const { dragMode } = api;

      if (isBackgroundPanningRef.current && onBackgroundPanMove && e.clientX != null && e.clientY != null) {
        onBackgroundPanMove({ clientX: e.clientX, clientY: e.clientY });
        return;
      }

      if (dragMode === DragMode.None) {
        // Drawing tools don't need hover feedback — only Pointer and Eraser do.
        if (api.activeTool !== POINTER_TOOL && api.activeTool !== ERASER_TOOL) {
          if (hoveredAnnotationId !== null) setHoveredAnnotationId(null);
          if (hoveredHandle !== null) setHoveredHandle(null);
          return;
        }
        const topHit = findTopmostHit({ annotations: api.annotations, hitParams: { pointerPosition: point, threshold } });
        setHoveredAnnotationId(topHit ? topHit.id : null);
        if (api.activeTool === POINTER_TOOL && api.selectedId) {
          const sel = api.annotations.find((a) => a.id === api.selectedId);
          if (sel) {
            const { handle, distance } = findHandleNear(point, sel);
            // Hover radius matches the click hit radius so the resize cursor and the grabable area are consistent.
            const handleHitBase = Math.max(threshold, hitThresholdBase * HANDLE_HIT_RADIUS);
            setHoveredHandle(handle && distance < handleHitBase ? handle : null);
          } else {
            setHoveredHandle(null);
          }
        } else {
          setHoveredHandle(null);
        }
        return;
      }

      if (dragMode === DragMode.Draw && drag.current.currentDraft) {
        const draft = drag.current.currentDraft;
        if (draft.type === AnnotationType.Pen || draft.type === AnnotationType.Highlighter) {
          const next: PenAnnotation = { ...(draft as PenAnnotation), points: [...(draft as PenAnnotation).points, point] };
          updateDraft(next);
        } else if (draft.type === AnnotationType.Arrow) {
          const ar = draft as ArrowAnnotation;
          let endPoint = point;
          if (e.shiftKey) {
            const dx = point.x - ar.start.x;
            const dy = point.y - ar.start.y;
            const angle = Math.atan2(dy, dx);
            const snap = Math.PI / 4;
            const snappedAngle = Math.round(angle / snap) * snap;
            const len = Math.hypot(dx, dy);
            endPoint = { x: ar.start.x + Math.cos(snappedAngle) * len, y: ar.start.y + Math.sin(snappedAngle) * len };
          }
          updateDraft({ ...ar, end: endPoint });
        } else if (draft.type === AnnotationType.Rectangle && drag.current.drawStartPoint) {
          const startPt = drag.current.drawStartPoint;
          let dx = point.x - startPt.x;
          let dy = point.y - startPt.y;
          if (e.shiftKey) {
            const size = Math.max(Math.abs(dx), Math.abs(dy));
            dx = size * Math.sign(dx || 1);
            dy = size * Math.sign(dy || 1);
          }
          const endX = startPt.x + dx;
          const endY = startPt.y + dy;
          updateDraft({
            ...(draft as RectangleAnnotation),
            origin: { x: Math.min(startPt.x, endX), y: Math.min(startPt.y, endY) },
            width: Math.abs(dx),
            height: Math.abs(dy),
          });
        } else if (draft.type === AnnotationType.Circle && drag.current.drawStartPoint) {
          const startPt = drag.current.drawStartPoint;
          let rx = Math.abs(point.x - startPt.x);
          let ry = Math.abs(point.y - startPt.y);
          if (e.shiftKey) {
            const r = Math.max(rx, ry);
            rx = r;
            ry = r;
          }
          updateDraft({ ...(draft as CircleAnnotation), radiusX: rx, radiusY: ry });
        }
        return;
      }

      if (dragMode === DragMode.Move && drag.current.dragStartSnapshot && drag.current.dragStartPoint && api.selectedId) {
        const moveParams: MoveAnnotationParams = {
          annotation: drag.current.dragStartSnapshot,
          deltaX: point.x - drag.current.dragStartPoint.x,
          deltaY: point.y - drag.current.dragStartPoint.y,
        };
        const moved = moveAnnotation(moveParams);
        api.setAnnotations(api.annotations.map((a) => (a.id === api.selectedId ? moved : a)));
        return;
      }

      if (dragMode === DragMode.Resize && drag.current.dragStartSnapshot && drag.current.activeHandle && api.selectedId) {
        let constrained = point;
        if (e.ctrlKey || e.metaKey) {
          const bbox = getBBox(drag.current.dragStartSnapshot);
          if (bbox && bbox.width > 1 && bbox.height > 1) {
            const aspect = bbox.width / bbox.height;
            const centerX = bbox.x + bbox.width / 2;
            const centerY = bbox.y + bbox.height / 2;
            const dx = point.x - centerX;
            const dy = point.y - centerY;
            if (Math.abs(dx) / aspect > Math.abs(dy)) {
              constrained = { x: point.x, y: centerY + (dx / aspect) * Math.sign(dy || 1) };
            } else {
              constrained = { x: centerX + dy * aspect * Math.sign(dx || 1), y: point.y };
            }
          }
        }
        const target = api.annotations.find((a) => a.id === api.selectedId);
        if (!target) return;
        const resizeParams: ResizeAnnotationParams = {
          annotation: target,
          handleId: drag.current.activeHandle,
          pointerPosition: constrained,
          dragStartSnapshot: drag.current.dragStartSnapshot,
          shiftKey: !!e.shiftKey,
        };
        const resized = resizeAnnotation(resizeParams);
        api.setAnnotations(api.annotations.map((a) => (a.id === api.selectedId ? resized : a)));
        return;
      }
    },
    [api, findHandleNear, hitThresholdBase, onBackgroundPanMove, threshold, updateDraft]
  );

  const handlePointerUp = useCallback(
    (e: { pointerId?: number } = {}) => {
      if (e.pointerId != null) pointerIdsRef.current.delete(e.pointerId);

      if (isBackgroundPanningRef.current) {
        isBackgroundPanningRef.current = false;
        onBackgroundPanEnd?.();
        return;
      }

      const { dragMode } = api;

      if (dragMode === DragMode.Draw && drag.current.currentDraft) {
        const draft = drag.current.currentDraft;
        let shouldKeep = true;
        if ((draft.type === AnnotationType.Pen || draft.type === AnnotationType.Highlighter) && (draft as PenAnnotation).points.length < 3) {
          shouldKeep = false;
        }
        if (draft.type === AnnotationType.Rectangle) {
          const r = draft as RectangleAnnotation;
          if (r.width < SHAPE_MIN_SIZE_FRACTION || r.height < SHAPE_MIN_SIZE_FRACTION) shouldKeep = false;
        }
        if (draft.type === AnnotationType.Circle) {
          const c = draft as CircleAnnotation;
          if (c.radiusX < SHAPE_MIN_SIZE_FRACTION || c.radiusY < SHAPE_MIN_SIZE_FRACTION) shouldKeep = false;
        }
        if (shouldKeep) {
          api.pushUndoSnapshot();
          api.setAnnotations([...api.annotations, draft]);
          api.setSelectedId(draft.id);
          // After a completed draw, auto-switch to Pointer so the user can immediately manipulate the new shape.
          api.setActiveTool(POINTER_TOOL);
        }
      }

      drag.current.dragStartSnapshot = null;
      drag.current.dragStartPoint = null;
      drag.current.activeHandle = null;
      drag.current.drawStartPoint = null;
      updateDraft(null);
      api.setDragMode(DragMode.None);
    },
    [api, onBackgroundPanEnd, updateDraft]
  );

  const handleDoubleClick = useCallback(
    (point: AnnotationPoint, e: { clientX: number; clientY: number }) => {
      const topHit = findTopmostHit({ annotations: api.annotations, hitParams: { pointerPosition: point, threshold } });
      if (topHit) {
        // Double-click on a Text annotation re-enters edit mode (focus the input + invoke keyboard).
        if (topHit.type === AnnotationType.Text) {
          api.setSelectedId(topHit.id);
          onRequestTextEditing({ annotationId: topHit.id, isNew: false, clientX: e.clientX, clientY: e.clientY, selectAll: true });
        }
        // Hits on non-Text annotations are swallowed — no zoom, no behaviour change.
        return;
      }
      onBackgroundDoubleClick?.({ clientX: e.clientX, clientY: e.clientY });
    },
    [api, onBackgroundDoubleClick, onRequestTextEditing, threshold]
  );

  return {
    hoveredAnnotationId,
    hoveredHandle,
    draftAnnotation,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handleDoubleClick,
  };
};
